/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
import React, { useMemo, useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";
import { Bar } from "react-chartjs-2";

// MUI
import { Card, CircularProgress } from "@mui/material";

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FDropdown from "components/Dropdown/FDropdown";
import MDButton from "components/MDButton";
import pxToRem from "assets/theme/functions/pxToRem";

// Constants
import Constants, { Colors, Common } from "utils/Constants";

// Constants
const DEFAULT_SCOPE_COLOR = Colors.PRIMARY || "#2196f3";
const FALLBACK_SCOPE_COLORS =
  Array.isArray(Colors.SCOPE_COLORS) && Colors.SCOPE_COLORS.length > 0
    ? Colors.SCOPE_COLORS
    : [DEFAULT_SCOPE_COLOR];
const PROJECT_MANAGEMENT_COPY = Common.PROJECT_MANAGEMENT_DASHBOARD;
const AVERAGE_LINE_COLOR = Colors.DARK_ORANGE || "#FFBC8A";
const MAX_DECIMALS = 2;
const MINUTES_PER_HOUR = 60;

// Utility functions
const sanitizeNumber = (value) => {
  if (Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const ensureMinutes = (value) => Math.max(0, sanitizeNumber(value));
const minutesToHours = (minutes) => ensureMinutes(minutes) / MINUTES_PER_HOUR;
const formatHours = (hours) => Number(hours).toFixed(MAX_DECIMALS);

const minutesToFormattedDuration = (minutesValue) => {
  const totalMinutes = Math.round(ensureMinutes(minutesValue));
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
};

const hoursToHoursMinutes = (hoursValue) => {
  const totalMinutes = Math.round(ensureMinutes(hoursValue) * MINUTES_PER_HOUR);
  if (totalMinutes <= 0) return "0h 0m";
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  return `${hours}h ${minutes}m`;
};

const resolveLegendColor = (dataset) => {
  const isVisibleColor = (color) =>
    typeof color === "string" &&
    color.trim().length > 0 &&
    color !== "transparent" &&
    color !== "rgba(0,0,0,0)";

  if (!dataset) return undefined;
  if (isVisibleColor(dataset.legendColor)) return dataset.legendColor;

  const { backgroundColor, borderColor } = dataset;

  if (isVisibleColor(backgroundColor)) return backgroundColor;
  if (Array.isArray(backgroundColor)) {
    const foundColor = backgroundColor.find((color) => isVisibleColor(color));
    if (foundColor) return foundColor;
  }
  if (isVisibleColor(borderColor)) return borderColor;
  return undefined;
};

// Custom plugin to draw average label on the line
const averageLineLabelPlugin = {
  id: "averageLineLabel",
  afterDatasetsDraw: (chart) => {
    const { ctx, chartArea, scales } = chart;

    const averageDataset = chart.data.datasets.find(
      (ds) => ds.type === "line" && ds.label?.toLowerCase().includes("average")
    );

    if (!averageDataset || !chartArea) return;

    const averageValue = averageDataset.data.find((val) => val != null);
    if (!averageValue) return;

    const yPos = scales.y.getPixelForValue(averageValue);
    const centerX = chartArea.left + (chartArea.right - chartArea.left) / 2;

    ctx.save();

    const label = `Avg: ${hoursToHoursMinutes(averageValue)}`;
    ctx.font = "bold 13px Arial";
    const textWidth = ctx.measureText(label).width;
    const padding = 8;
    const rectWidth = textWidth + padding * 2;
    const rectHeight = 22;
    const rectX = centerX - rectWidth / 2;
    const rectY = yPos - rectHeight / 2;

    // Draw background with border
    ctx.fillStyle = "white";
    ctx.strokeStyle = averageDataset.borderColor || AVERAGE_LINE_COLOR;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.roundRect(rectX, rectY, rectWidth, rectHeight, 4);
    ctx.fill();
    ctx.stroke();

    // Draw text
    ctx.fillStyle = averageDataset.borderColor || AVERAGE_LINE_COLOR;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, centerX, yPos);

    ctx.restore();
  },
};

// Register Chart.js plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Title,
  ChartDataLabels,
  zoomPlugin,
  averageLineLabelPlugin
);

// Normalize location scopes data
const normalizeLocationScopes = (locations) => {
  if (!Array.isArray(locations)) return [];

  return locations.map((locationItem, locationIndex) => {
    const scopes = Array.isArray(locationItem?.scopes) ? locationItem.scopes : [];

    const normalizedScopes = scopes
      .map((scopeItem, scopeIndex) => {
        if (!scopeItem || typeof scopeItem !== "object") return null;

        const scopeIdCandidate =
          scopeItem?.scopeId ||
          scopeItem?.[Constants.MONGOOSE_ID] ||
          scopeItem?.id ||
          `scope-${locationIndex + 1}-${scopeIndex + 1}`;

        const scopeNameCandidate =
          typeof scopeItem?.scopeName === "string" && scopeItem.scopeName.trim().length > 0
            ? scopeItem.scopeName.trim()
            : scopeItem?.name || scopeItem?.title || `Scope ${scopeIndex + 1}`;

        const totalMinutes = ensureMinutes(scopeItem?.totalMinutes);
        const formattedDuration =
          typeof scopeItem?.formatted === "string" && scopeItem.formatted.length > 0
            ? scopeItem.formatted
            : minutesToFormattedDuration(totalMinutes);

        let sortOrder = sanitizeNumber(scopeItem?.sortOrder);
        if (!Number.isFinite(sortOrder) || sortOrder === 0) {
          const fallbackOrder = sanitizeNumber(scopeItem?.order);
          sortOrder =
            Number.isFinite(fallbackOrder) && fallbackOrder !== 0 ? fallbackOrder : Infinity;
        }

        return {
          scopeId: scopeIdCandidate.toString(),
          scopeName: scopeNameCandidate,
          totalMinutes,
          colorCode: scopeItem?.colorCode,
          formatted: formattedDuration,
          sortOrder,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const orderDelta = (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity);
        return orderDelta !== 0 ? orderDelta : a.scopeName.localeCompare(b.scopeName);
      });

    return {
      locationId: locationItem?.locationId,
      locationName:
        (typeof locationItem?.locationName === "string" &&
        locationItem.locationName.trim().length > 0
          ? locationItem.locationName.trim()
          : locationItem?.name || locationItem?.title) || `Location ${locationIndex + 1}`,
      scopes: normalizedScopes,
    };
  });
};

function ProjectHoursBarChart({
  projectOptions,
  selectedProject,
  onProjectChange,
  locationScopeData,
  zeroHoursOnly,
  loadingStatus,
  errorMessage,
  showProjectSelector,
}) {
  const chartRef = useRef(null);
  const chartAreaRef = useRef(null);
  const summaryRef = useRef(null);
  const resizeRafRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(520);

  const normalizedLocationScopeData = useMemo(
    () => normalizeLocationScopes(locationScopeData),
    [locationScopeData]
  );

  const handleProjectChange = useCallback(
    (_, value) => {
      onProjectChange?.("project", value);
    },
    [onProjectChange]
  );

  // Build chart data
  const { labels, datasets, averageHours, totalHours, hasRenderableData, maxBarValue } =
    useMemo(() => {
      if (!Array.isArray(normalizedLocationScopeData) || normalizedLocationScopeData.length === 0) {
        return {
          labels: [],
          datasets: [],
          averageHours: 0,
          totalHours: 0,
          hasRenderableData: false,
          maxBarValue: 0,
        };
      }

      const locationLabels = normalizedLocationScopeData.map(
        (locationEntry, index) =>
          locationEntry?.locationName || locationEntry?.name || `Location ${index + 1}`
      );
      const totalsPerLocation = new Array(locationLabels.length).fill(0);

      // Build scope definitions map
      const scopeDefinitions = new Map();
      normalizedLocationScopeData.forEach((locationEntry) => {
        (locationEntry?.scopes || []).forEach((scopeItem) => {
          if (!scopeItem) return;
          const scopeName = scopeItem.scopeName || scopeItem.name || "Uncategorised";
          const existing = scopeDefinitions.get(scopeName);
          const sortOrder = Number.isFinite(scopeItem?.sortOrder) ? scopeItem.sortOrder : Infinity;

          if (!existing) {
            scopeDefinitions.set(scopeName, {
              colorCode: scopeItem?.colorCode,
              sortOrder,
            });
          } else {
            if (!existing.colorCode && scopeItem?.colorCode) {
              existing.colorCode = scopeItem.colorCode;
            }
            if (sortOrder < (existing.sortOrder ?? Infinity)) {
              existing.sortOrder = sortOrder;
            }
          }
        });
      });

      // Sort scopes
      const orderedScopes = Array.from(scopeDefinitions.entries())
        .sort((a, b) => {
          const orderDelta = (a[1].sortOrder ?? Infinity) - (b[1].sortOrder ?? Infinity);
          return orderDelta !== 0 ? orderDelta : a[0].localeCompare(b[0]);
        })
        .map(([scopeName, meta]) => ({
          scopeName,
          colorCode: meta.colorCode,
          sortOrder: meta.sortOrder,
        }));

      if (orderedScopes.length === 0) {
        return {
          labels: locationLabels,
          datasets: [],
          averageHours: 0,
          totalHours: 0,
          hasRenderableData: false,
          maxBarValue: 0,
        };
      }

      // Initialize datasets
      const scopeSeriesMap = new Map();
      orderedScopes.forEach((scopeConfig, scopeIndex) => {
        const preferredColor =
          scopeConfig.colorCode ||
          FALLBACK_SCOPE_COLORS[scopeIndex % FALLBACK_SCOPE_COLORS.length] ||
          DEFAULT_SCOPE_COLOR;

        scopeSeriesMap.set(scopeConfig.scopeName, {
          label: scopeConfig.scopeName,
          stack: "hours",
          type: "bar",
          borderRadius: 6,
          minBarLength: 4,
          data: new Array(locationLabels.length).fill(0),
          backgroundColor: new Array(locationLabels.length).fill(preferredColor),
          borderColor: preferredColor,
          borderWidth: preferredColor ? 1 : 0,
          customMeta: new Array(locationLabels.length).fill(null),
          legendColor: preferredColor,
        });
      });

      // Populate data
      normalizedLocationScopeData.forEach((locationEntry, locationIndex) => {
        const locationLabel = locationLabels[locationIndex];
        const scopes = Array.isArray(locationEntry?.scopes) ? locationEntry.scopes : [];
        const scopeMap = new Map(
          scopes.map((s) => [s?.scopeName || s?.name || "Uncategorised", s])
        );

        orderedScopes.forEach((scopeConfig, scopeIndex) => {
          const dataset = scopeSeriesMap.get(scopeConfig.scopeName);
          if (!dataset) return;

          const scopeItem = scopeMap.get(scopeConfig.scopeName);
          if (!scopeItem) {
            dataset.data[locationIndex] = 0;
            dataset.customMeta[locationIndex] = null;
            return;
          }

          const totalMinutes = ensureMinutes(scopeItem.totalMinutes);
          const hoursValue = minutesToHours(totalMinutes);
          const roundedHours = Number(hoursValue.toFixed(MAX_DECIMALS));

          if (roundedHours <= 0) {
            dataset.data[locationIndex] = 0;
            dataset.customMeta[locationIndex] = null;
            return;
          }

          const formattedDuration =
            scopeItem?.formatted || minutesToFormattedDuration(totalMinutes);
          const baseColor =
            scopeItem?.colorCode ||
            scopeConfig.colorCode ||
            FALLBACK_SCOPE_COLORS[scopeIndex % FALLBACK_SCOPE_COLORS.length] ||
            DEFAULT_SCOPE_COLOR;

          dataset.data[locationIndex] = roundedHours;
          dataset.customMeta[locationIndex] = {
            originalHours: roundedHours,
            minutes: totalMinutes,
            formatted: formattedDuration,
            locationName: locationLabel,
            scopeName: scopeConfig.scopeName,
            color: baseColor,
          };
          dataset.backgroundColor[locationIndex] = baseColor;
          totalsPerLocation[locationIndex] += roundedHours;
        });
      });

      // Calculate averages and totals
      const grandTotal = totalsPerLocation.reduce((sum, value) => sum + value, 0);
      const locationsWithHours = totalsPerLocation.filter((value) => value > 0).length;
      const divisor =
        locationsWithHours > 0 ? locationsWithHours : Math.max(locationLabels.length, 1);
      const computedAverage = divisor > 0 ? grandTotal / divisor : 0;
      const adjustedAverage =
        grandTotal === 0 ? 0 : Number.isFinite(computedAverage) ? computedAverage : 0;
      const calculatedMaxBarValue = Math.max(...totalsPerLocation, 0);

      const chartDatasets = Array.from(scopeSeriesMap.values());

      // Add padding and average line
      if (locationLabels.length > 0) {
        const extendedLabels = ["", ...locationLabels, ""];

        chartDatasets.forEach((dataset) => {
          if (dataset.type !== "line") {
            dataset.data = [null, ...dataset.data, null];
            if (Array.isArray(dataset.backgroundColor)) {
              dataset.backgroundColor = [null, ...dataset.backgroundColor, null];
            }
            if (Array.isArray(dataset.customMeta)) {
              dataset.customMeta = [null, ...dataset.customMeta, null];
            }
          }
        });

        chartDatasets.push({
          label: PROJECT_MANAGEMENT_COPY.AVERAGE_REFERENCE_LABEL.trim(),
          type: "line",
          data: extendedLabels.map(() => adjustedAverage),
          borderColor: AVERAGE_LINE_COLOR,
          borderWidth: 3,
          borderDash: [6, 6],
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          fill: false,
          yAxisID: "y",
          order: -1,
          legendColor: AVERAGE_LINE_COLOR,
        });

        return {
          labels: extendedLabels,
          datasets: chartDatasets,
          averageHours: adjustedAverage,
          totalHours: grandTotal,
          hasRenderableData: true,
          maxBarValue: calculatedMaxBarValue,
        };
      }

      return {
        labels: locationLabels,
        datasets: chartDatasets,
        averageHours: adjustedAverage,
        totalHours: grandTotal,
        hasRenderableData: false,
        maxBarValue: calculatedMaxBarValue,
      };
    }, [normalizedLocationScopeData]);

  // Chart options
  const yAxisMax = useMemo(
    () => Math.max(maxBarValue, Number.isFinite(averageHours) ? averageHours : 0) + 5,
    [maxBarValue, averageHours]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "nearest",
        intersect: true,
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            generateLabels: (chart) => {
              const generator =
                ChartJS?.defaults?.plugins?.legend?.labels?.generateLabels ?? (() => []);
              const filteredItems = generator(chart).filter((labelItem) => {
                const dataset = chart.data?.datasets?.[labelItem.datasetIndex];
                return (
                  dataset?.label?.trim() !== PROJECT_MANAGEMENT_COPY.AVERAGE_REFERENCE_LABEL.trim()
                );
              });
              return filteredItems.map((labelItem) => {
                const dataset = chart.data?.datasets?.[labelItem.datasetIndex];
                const legendColor = resolveLegendColor(dataset);
                if (legendColor) {
                  labelItem.fillStyle = legendColor;
                  labelItem.strokeStyle = legendColor;
                }
                return labelItem;
              });
            },
          },
        },
        tooltip: {
          mode: "nearest",
          intersect: true,
          padding: 14,
          caretSize: 7,
          cornerRadius: 6,
          bodyFont: { size: 15, weight: "600" },
          titleFont: { size: 15, weight: "700" },
          callbacks: {
            title: (items) => {
              const item = items?.[0];
              if (!item) return "";

              if (item.dataset?.type === "line") {
                return item?.label || item?.dataset?.label || "";
              }

              const meta = item?.dataset?.customMeta?.[item?.dataIndex ?? -1];
              if (meta?.locationName) {
                return `${PROJECT_MANAGEMENT_COPY.TOOLTIP_LOCATION_PREFIX} ${meta.locationName}`;
              }
              if (item?.label) {
                return `${PROJECT_MANAGEMENT_COPY.TOOLTIP_LOCATION_PREFIX} ${item.label}`;
              }
              return "";
            },
            label: (context) => {
              if (context.dataset.type === "line") {
                return `${context.dataset.label}: ${formatHours(context.parsed.y)} hrs`;
              }
              const meta = context.dataset?.customMeta?.[context.dataIndex ?? -1];
              const scopeLabel = meta?.scopeName || context.dataset.label;
              return scopeLabel
                ? `${PROJECT_MANAGEMENT_COPY.TOOLTIP_SCOPE_PREFIX} ${scopeLabel}`
                : "";
            },
            afterLabel: (context) => {
              if (context.dataset.type === "line") return "";
              const meta = context.dataset?.customMeta?.[context.dataIndex ?? -1];
              const formatted = meta?.formatted || `${formatHours(context.parsed.y)} hrs`;
              return `${PROJECT_MANAGEMENT_COPY.TOOLTIP_HOURS_PREFIX} ${formatted}`;
            },
          },
        },
        datalabels: {
          align: "center",
          anchor: "center",
          font: {
            size: 0,
            weight: 600,
          },
          formatter: (_, ctx) => {
            if (ctx?.dataset?.type === "line") return "";
            const meta = ctx?.dataset?.customMeta?.[ctx.dataIndex ?? -1];
            const originalHours = Number(meta?.originalHours);
            return Number.isFinite(originalHours) && originalHours > 0
              ? `${formatHours(originalHours)}h`
              : "";
          },
          display: (ctx) => {
            if (ctx?.dataset?.type === "line") return false;
            const meta = ctx?.dataset?.customMeta?.[ctx.dataIndex ?? -1];
            const originalHours = Number(meta?.originalHours);
            return Number.isFinite(originalHours) && originalHours > 0;
          },
          padding: {
            top: 0,
            bottom: 0,
          },
          clip: false,
        },
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
            modifierKey: "shift",
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            drag: {
              enabled: true,
              backgroundColor: "rgba(33, 150, 243, 0.15)",
              borderColor: Colors.PRIMARY || "#2196f3",
              borderWidth: 1,
            },
            mode: "x",
            overScaleMode: "x",
          },
          limits: {
            x: {
              minRange: 1,
            },
            y: {
              min: 0,
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          offset: false,
          grid: {
            display: true,
            drawOnChartArea: true,
            drawTicks: true,
            color(context) {
              const { index } = context;
              const ticksLength = context.chart.scales.x.ticks.length;
              return index === 0 || index === ticksLength - 1
                ? "transparent"
                : "rgba(0, 0, 0, 0.1)";
            },
          },
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 0,
            padding: 0,
            callback(value) {
              // eslint-disable-next-line react/no-this-in-sfc
              const label = this.getLabelForValue(value);
              return label === "" ? "" : label;
            },
          },
        },
        y: {
          stacked: true,
          title: {
            display: true,
            text: PROJECT_MANAGEMENT_COPY.CHART_Y_AXIS_LABEL,
          },
          beginAtZero: true,
          suggestedMax: Number.isFinite(yAxisMax) ? yAxisMax : undefined,
        },
      },
    }),
    [yAxisMax]
  );

  const handleResetZoom = useCallback(() => {
    chartRef.current?.resetZoom?.();
  }, []);

  const updateChartHeight = useCallback(() => {
    if (typeof window === "undefined") return;

    if (resizeRafRef.current) {
      cancelAnimationFrame(resizeRafRef.current);
    }

    resizeRafRef.current = window.requestAnimationFrame(() => {
      const chartNode = chartAreaRef.current;
      if (!chartNode) return;

      const viewportHeight = window.innerHeight || 0;
      const rect = chartNode.getBoundingClientRect();
      const computedStyles = window.getComputedStyle(chartNode);
      const paddingTop = parseFloat(computedStyles.paddingTop || "0");
      const paddingBottom = parseFloat(computedStyles.paddingBottom || "0");
      const topOffset = rect.top + paddingTop;
      const footerHeight = summaryRef.current?.getBoundingClientRect().height || 0;
      const spacingAllowance = paddingBottom + footerHeight + 64;

      const availableHeight = viewportHeight - topOffset - spacingAllowance;
      if (!Number.isFinite(availableHeight)) {
        setChartHeight(520);
        return;
      }

      const minHeight = 260;
      const maxHeight = Math.max(minHeight, viewportHeight - 140);
      const nextHeight =
        availableHeight >= minHeight
          ? Math.min(availableHeight, maxHeight)
          : Math.max(160, availableHeight);

      setChartHeight(Math.max(160, Math.round(nextHeight)));
    });
  }, []);

  useEffect(() => {
    updateChartHeight();
    if (typeof window === "undefined") return () => {};

    window.addEventListener("resize", updateChartHeight);
    return () => {
      window.removeEventListener("resize", updateChartHeight);
    };
  }, [updateChartHeight]);

  useEffect(() => {
    updateChartHeight();
  }, [
    updateChartHeight,
    labels.length,
    datasets.length,
    loadingStatus,
    hasRenderableData,
    errorMessage,
  ]);

  useEffect(
    () => () => {
      if (resizeRafRef.current && typeof window !== "undefined") {
        cancelAnimationFrame(resizeRafRef.current);
      }
    },
    []
  );

  const isLoading =
    loadingStatus === Constants.PENDING ||
    (typeof loadingStatus === "string" && loadingStatus.toLowerCase() === "pending");
  const hasError = Boolean(errorMessage) && !isLoading;
  const noProjectSelected = !selectedProject;
  const zeroHoursMessage = PROJECT_MANAGEMENT_COPY.ZERO_HOURS_MESSAGE;
  const emptyStateMessage = noProjectSelected
    ? PROJECT_MANAGEMENT_COPY.SELECT_PROJECT_MESSAGE
    : zeroHoursOnly
    ? zeroHoursMessage
    : PROJECT_MANAGEMENT_COPY.NO_DATA_MESSAGE;

  const resolvedChartHeight = Math.max(200, Math.round(chartHeight || 0));

  return (
    <Card
      sx={{
        padding: pxToRem(5),
        borderRadius: pxToRem(16),
        minHeight: "auto",
      }}
    >
      <MDBox
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: pxToRem(16),
          marginBottom: pxToRem(16),
        }}
      >
        <MDTypography
          sx={{
            fontWeight: 600,
            fontSize: pxToRem(15),
            color: Colors.PRIMARY,
          }}
        >
          {PROJECT_MANAGEMENT_COPY.HOURS_WORKED_TITLE}
        </MDTypography>
        <MDBox
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: pxToRem(12),
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          {showProjectSelector && (
            <MDBox width={pxToRem(260)}>
              <FDropdown
                id="project-scope-hours"
                name="project"
                menu={projectOptions}
                value={selectedProject}
                handleChange={handleProjectChange}
                placeholder={PROJECT_MANAGEMENT_COPY.PROJECT_PLACEHOLDER}
              />
            </MDBox>
          )}
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={handleResetZoom}
            sx={{ minWidth: pxToRem(120) }}
          >
            {PROJECT_MANAGEMENT_COPY.RESET_ZOOM_BUTTON}
          </MDButton>
        </MDBox>
      </MDBox>

      <MDTypography
        sx={{
          fontSize: pxToRem(16),
          fontWeight: 500,
          color: Colors.DARK_GREY,
          marginBottom: pxToRem(8),
        }}
      >
        {PROJECT_MANAGEMENT_COPY.LOCATION_SCOPE_SUBTITLE}
      </MDTypography>

      <MDBox
        ref={chartAreaRef}
        sx={{
          position: "relative",
          minHeight: `${resolvedChartHeight}px`,
          height: `${resolvedChartHeight}px`,
        }}
      >
        {isLoading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
            <CircularProgress size={36} />
          </MDBox>
        ) : hasError ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
            <MDTypography
              sx={{
                color: Colors.DARK_GREY,
                fontSize: pxToRem(16),
              }}
            >
              {errorMessage}
            </MDTypography>
          </MDBox>
        ) : hasRenderableData ? (
          <Bar
            ref={chartRef}
            data={{ labels, datasets }}
            options={chartOptions}
            height={resolvedChartHeight}
          />
        ) : (
          <MDBox display="flex" justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
            <MDTypography
              sx={{
                color: Colors.DARK_GREY,
                fontSize: pxToRem(16),
              }}
            >
              {emptyStateMessage}
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      <MDBox
        ref={summaryRef}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          gap: pxToRem(16),
          marginTop: pxToRem(24),
        }}
      >
        <MDTypography
          sx={{
            fontSize: pxToRem(16),
            fontWeight: 600,
          }}
        >
          {PROJECT_MANAGEMENT_COPY.AVERAGE_REFERENCE_LABEL} {hoursToHoursMinutes(averageHours)}
        </MDTypography>
        <MDTypography
          sx={{
            fontSize: pxToRem(16),
            fontWeight: 600,
          }}
        >
          {PROJECT_MANAGEMENT_COPY.TOTAL_HOURS_LABEL} {hoursToHoursMinutes(totalHours)}
        </MDTypography>
      </MDBox>
    </Card>
  );
}

const menuShape = PropTypes.arrayOf(
  PropTypes.shape({
    [Constants.MONGOOSE_ID]: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
  })
);

ProjectHoursBarChart.propTypes = {
  projectOptions: menuShape,
  selectedProject: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onProjectChange: PropTypes.func,
  locationScopeData: PropTypes.arrayOf(PropTypes.object),
  zeroHoursOnly: PropTypes.bool,
  loadingStatus: PropTypes.string,
  errorMessage: PropTypes.string,
  showProjectSelector: PropTypes.bool,
};

ProjectHoursBarChart.defaultProps = {
  projectOptions: [],
  selectedProject: "",
  onProjectChange: undefined,
  locationScopeData: [],
  zeroHoursOnly: false,
  loadingStatus: "",
  errorMessage: "",
  showProjectSelector: true,
};

export default ProjectHoursBarChart;
